/**
 * /landing-v34-preview — FINAL consolidated mockup.
 *
 * Slim v34 structure (compressed padding, merged sections, horizontal dev strip)
 * PLUS Cloud Integrations (per-cloud feature depth) PLUS Complete Product Surface
 * (8-pillar capability map). One single preview the user can sign off on.
 *
 * Production Landing.js is untouched until the user approves.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Brain, ArrowRightLeft, Users, ArrowRight, Sparkles, Code2,
  Bot, GitBranch, Workflow, Eye, Zap, Play, Calculator,
  ShieldCheck, TrendingUp, Cpu, Layers, Flame,
  Target, ChevronDown, Cloud, Shield, AlertOctagon, BarChart3,
  Leaf, Presentation, Tags, Bell, Gauge,
} from 'lucide-react';

const LandingV34Preview = () => {
  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="landing-v34-preview-page">
      {/* ───── PREVIEW BANNER ───── */}
      <div className="sticky top-0 z-50 bg-emerald-500 text-slate-950 px-6 py-2 flex items-center justify-between flex-wrap gap-2 shadow-lg">
        <div className="flex items-center gap-2 text-xs font-bold">
          <Eye className="w-3.5 h-3.5" /> v34 FINAL PREVIEW · everything included · production untouched
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span>Slim padding · Cloud Integrations · Complete Product Surface</span>
          <Link to="/" className="bg-slate-950 text-emerald-300 rounded-full px-3 py-1 font-semibold hover:bg-slate-900">Open live /</Link>
        </div>
      </div>

      {/* ───── 1. NAV ───── */}
      <nav className="px-6 py-4 border-b border-white/5 sticky top-9 z-40 bg-slate-950/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Zap className="w-5 h-5 text-slate-950" />
            </div>
            <span className="text-xl font-bold">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-300">
            <span>AI Cost Calculator</span>
            <span>Reality Check</span>
            <span>Engines <span className="text-[10px] bg-emerald-500/20 text-emerald-300 rounded-full px-1.5">26</span></span>
            <span>VS Code <span className="text-[9px] bg-amber-500/20 text-amber-300 rounded-full px-1.5">BETA</span></span>
            <span>Pricing</span>
            <span>Resources</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">Sign In</span>
            <button className="bg-emerald-500 text-slate-950 px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-1.5 shadow-lg shadow-emerald-500/30">
              <Play className="w-3 h-3 fill-current" /> Live Demo
            </button>
          </div>
        </div>
      </nav>

      {/* ───── 2. HERO ───── */}
      <section className="relative pt-16 pb-12 px-4 overflow-hidden" style={{ background: 'radial-gradient(ellipse at top, rgba(16,185,129,0.08) 0%, transparent 60%)' }}>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-4 py-1.5 mb-4">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-xs text-emerald-200 font-semibold">SOC 2 · GDPR · 27 patent-pending engines</span>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/40 rounded-full px-3 py-1">
                <span className="bg-emerald-500 text-slate-950 text-[10px] font-mono font-bold tracking-wider rounded-full px-1.5">NEW</span>
                <span className="text-emerald-200 text-xs">Token Intelligence Suite</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-rose-500/10 border border-rose-400/40 rounded-full px-3 py-1">
                <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span></span>
                <span className="text-rose-200 text-xs">Live burn ticker · Reasoning Efficiency</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 leading-[1.05] tracking-tight">
              The <span className="text-emerald-300 drop-shadow-[0_0_24px_rgba(16,185,129,0.45)]">FinOps tool</span><br />
              engineers don't hate.
            </h1>
            <p className="text-lg text-slate-300 mb-7 max-w-xl">
              Cut your cloud + AI bill 20-40% without slowing a single deploy. 26 proprietary engines that do the math, the orchestration, and the explaining.
            </p>
            <div className="flex flex-wrap gap-3">
              <button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-7 py-3.5 rounded-xl shadow-lg shadow-emerald-500/30 inline-flex items-center gap-2">
                <Play className="w-4 h-4 fill-current" /> Try live demo (no signup)
              </button>
              <button className="border border-emerald-400/40 hover:bg-emerald-500/10 text-emerald-200 font-semibold px-7 py-3.5 rounded-xl inline-flex items-center gap-2">
                <Calculator className="w-4 h-4" /> Run AI Cost Calculator
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-5 italic">Stateless · read-only 2-min audit · numbers within 60 seconds.</p>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-emerald-500/15 rounded-3xl blur-2xl pointer-events-none" />
            <div className="relative rounded-2xl border border-emerald-400/30 bg-slate-900/80 backdrop-blur-md p-6 shadow-2xl">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono mb-3">Dashboard · 5/24/2026</div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3">
                  <div className="text-[10px] text-emerald-300 uppercase font-bold">Savings YTD</div>
                  <div className="text-2xl font-extrabold text-emerald-300">$148,210</div>
                </div>
                <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-3">
                  <div className="text-[10px] text-rose-300 uppercase font-bold">Burning now</div>
                  <div className="text-2xl font-extrabold text-rose-300 tabular-nums">$4.20/hr</div>
                </div>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between rounded bg-amber-500/5 border border-amber-500/20 p-2">
                  <span className="text-amber-200">Retry storm · /chat</span>
                  <span className="text-amber-300 font-mono">+$612/mo</span>
                </div>
                <div className="flex items-center justify-between rounded bg-emerald-500/10 border border-emerald-500/30 p-2">
                  <span className="text-emerald-200">Smart Router → Haiku 4.5</span>
                  <span className="text-emerald-300 font-mono">−$412/mo</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <a className="hidden md:inline-flex absolute left-1/2 -translate-x-1/2 bottom-4 items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-100 text-xs font-semibold">
          <Play className="w-3.5 h-3.5 fill-current" />
          Skip to the 30-second walkthrough
          <ChevronDown className="w-3.5 h-3.5 animate-bounce" />
        </a>
      </section>

      {/* ───── 3. ★ FIRST-TO-MARKET (promoted to fold) ───── */}
      <section className="py-12 px-4 border-t border-emerald-500/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent" />
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
              <Sparkles className="w-3 h-3" /> First-to-market · May 2026
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] text-white">
              Three things <span className="text-emerald-400">nobody else</span> measures.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { Icon: Brain, tag: 'FIRST EVER', title: 'Reasoning Efficiency Engine', body: 'Token counters count tokens. CARTIE measures whether the tokens resolved an intent. Cost-per-resolved-intent · loop-waste %.' },
              { Icon: ArrowRightLeft, tag: 'LIVE QUOTES', title: 'Cross-Cloud Token Arbitrage', body: 'LLM prices fluctuate per provider like a stock market. Auto-route every intent to the cheapest quality-matched provider.' },
              { Icon: Users, tag: 'INVISIBLE SPEND', title: 'Shadow AI SaaS Audit', body: 'Okta + Rippling SCIM → every Notion AI, Canva AI, Copilot seat on a corp card. Find the $15k/mo your CFO can\'t see.' },
            ].map((c, i) => (
              <div key={i} className="rounded-2xl border border-emerald-500/20 bg-slate-900/60 p-6 hover:border-emerald-400/60 hover:-translate-y-0.5 transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                    <c.Icon className="w-5 h-5 text-slate-950" />
                  </div>
                  <span className="text-[9px] font-bold tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">{c.tag}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1.5">{c.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── 4. ★ CLOUD INTEGRATIONS (per-cloud depth) ───── */}
      <section className="py-12 px-4 border-t border-white/5 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
              <Cloud className="w-3 h-3" /> 6 live · 9 integrating · read-only · 2-min setup
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] text-white">
              One dashboard for <span className="text-emerald-400">every cloud you use</span>.
            </h2>
            <p className="mt-3 text-base text-slate-300">Native, deep integrations — not surface-level scrapes. The right API per provider · credentials never stored.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { id: 'aws', name: 'AWS', tag: 'LIVE', desc: 'Cost Explorer + CUR. EC2 / RDS / S3 / Lambda / NAT / EBS rightsizing. Spot intelligence. RI & Savings Plan optimizer.', features: ['Cost Explorer API', 'Anomaly v2', 'RI / SP optimizer', 'Right-sizing'] },
              { id: 'azure', name: 'Azure', tag: 'LIVE', desc: 'Cost Management API + CSV ingestion. Hybrid Benefit, RI optimizer, regional drift, subscription rollup.', features: ['Cost Mgmt API', 'CSV ingestion', 'Hybrid Benefit', 'RI optimizer'] },
              { id: 'gcp', name: 'Google Cloud', tag: 'LIVE', desc: 'BigQuery billing export. CUDs / sustained-use optimization. Per-project / per-folder roll-up & forecasting.', features: ['Billing Export', 'CUD optimizer', 'BigQuery costs', 'Forecasting'] },
              { id: 'snowflake', name: 'Snowflake', tag: 'LIVE', desc: 'Account Usage queries. Warehouse rightsizing, auto-suspend tuning, query-cost attribution, materialized-view ROI.', features: ['Warehouse rightsize', 'Query autopsy', 'Auto-suspend', 'MV ROI'] },
              { id: 'databricks', name: 'Databricks', tag: 'LIVE', desc: 'Cluster rightsize, Photon ROI, spot policy, autoscaling tuning, job-level cost attribution.', features: ['Cluster rightsize', 'Photon ROI', 'Spot policy', 'Job costs'] },
              { id: 'do', name: 'DigitalOcean', tag: 'LIVE', desc: 'Droplet, App Platform, K8s & Spaces cost analysis. Idle droplet hunter + reserved IP cleanup.', features: ['Droplet rightsize', 'K8s costs', 'Idle hunter', 'Volume cleanup'] },
              { id: 'k8s', name: 'Kubernetes', tag: 'LIVE', desc: 'Monte-Carlo Shapley attribution across namespaces. Idle pod hunter, request/limit drift, cost-fair chargebacks.', features: ['Shapley attribution', 'Idle pods', 'Namespace splits', 'Chargebacks'] },
              { id: 'enterprise', name: 'Oracle · IBM · Alibaba', tag: 'SOON', desc: 'Enterprise-tier providers under integration. Production roll-out queued behind the May-2026 launch milestone.', features: ['Oracle Cloud', 'IBM Cloud', 'Alibaba Cloud'] },
              { id: 'edge', name: 'Edge & CDN · Storage', tag: 'SOON', desc: 'Cloudflare, Linode/Akamai, Hetzner, OVHcloud, Vultr, Wasabi. Egress + object-storage cost attribution.', features: ['Cloudflare', 'Hetzner / OVH', 'Wasabi · R2'] },
            ].map((c) => {
              const isLive = c.tag === 'LIVE';
              return (
                <div key={c.id} className={`rounded-2xl border ${isLive ? 'border-emerald-500/25 bg-slate-900/60 hover:border-emerald-400/60' : 'border-amber-500/25 bg-slate-900/40 hover:border-amber-400/60'} p-5 hover:-translate-y-0.5 transition-all`}>
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <h3 className="text-base font-bold text-white">{c.name}</h3>
                    <span className={`shrink-0 text-[9px] font-bold tracking-widest rounded-full px-2 py-0.5 border ${isLive ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-amber-500/10 border-amber-500/30 text-amber-300'}`}>
                      {isLive ? '● LIVE' : 'SOON'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed mb-3">{c.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {c.features.map((f) => (
                      <span key={f} className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${isLive ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20' : 'bg-amber-500/5 text-amber-300/80 border border-amber-500/20'}`}>{f}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 text-slate-950 font-semibold text-sm">Open Integrations Hub <ArrowRight className="w-4 h-4" /></div>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/30 text-emerald-200 font-medium text-sm">
              <Shield className="w-3.5 h-3.5" /> Stateless audit · read-only IAM
            </div>
          </div>
        </div>
      </section>

      {/* ───── 5. ★ COMPLETE PRODUCT SURFACE (8 pillars) ───── */}
      <section className="py-12 px-4 border-t border-white/5 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
              <Gauge className="w-3 h-3" /> Complete coverage · 8 pillars · 70+ surfaces
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] text-white">
              The whole FinOps stack <span className="text-emerald-400">in one tool</span>.
            </h2>
            <p className="mt-3 text-base text-slate-300">Replace 6 vendors (Vantage + CloudHealth + Kubecost + Helicone + Datadog + Watershed).</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { Icon: Brain, pillar: 'AI / LLM Suite', items: ['Token Intelligence Suite', 'LLM Proxy + Smart Router', 'Prompt Library + Playground', 'Eval Runner + QA Failover', 'Fine-Tuning ROI', 'AI Feature P&L', 'Reasoning Efficiency', 'AI Chat Advisor + "Why?" explainer'] },
              { Icon: AlertOctagon, pillar: 'Anomaly & Causality', items: ['Anomaly Detection v2 + Auto-resolve', 'Spike Autopsy', 'Invoice Autopsy', 'Causal FinOps Bot', 'Cost Bisect (git-bisect for $)', 'Token Leak Detector', 'Bill Shock Prevention', 'Spend Freeze (emergency stop)'] },
              { Icon: BarChart3, pillar: 'Forecasting & Simulation', items: ['Cost Forecasting + Forward Predict', 'Decision Simulator', 'What-If Calculator', 'Cost Time Machine', 'PR Cost Predictor (Terraform)', 'Egress Predictor', 'Egress Shield (live flow map)'] },
              { Icon: Cpu, pillar: 'Cloud Cost', items: ['AWS · Azure · GCP', 'Snowflake · Databricks', 'DigitalOcean · K8s', 'RI/SP Commitment Coach', 'Zombie Killer + Storage Detective', 'Resource Scheduler', 'SaaS Detective', 'Recommendations + One-Click Fix'] },
              { Icon: Leaf, pillar: 'Carbon & ESG', items: ['Carbon Cost Pareto', 'Carbon Optimizer (active scheduling)', 'Scope 3 emissions', 'Region × CO₂ optimizer', 'ESG-ready reports'] },
              { Icon: Presentation, pillar: 'CFO Tools', items: ['AI CFO Advisor + Command Center', 'Board Deck Generator', 'Vendor Deal Intel', 'Customer P&L · Unit Econ', 'Cost Scorecard (gamified rankings)', 'Cost Allocation · Cost Centers', 'Virtual Tenant (header chargebacks)', 'Industry Intel · Multi-currency'] },
              { Icon: Tags, pillar: 'Tags & Compliance', items: ['Smart Tag Fixer', 'Tag Canon · Tag Drift', 'Compliance Mapper (SOC 2 / ISO)', 'Algorithm Correctness manifest', 'SOC 2 Readiness', 'Audit Export'] },
              { Icon: Bell, pillar: 'Notifications & Surfaces', items: ['Slack @cartie bot', 'Monday Brief · Weekly Digest', 'Voice Assistant', 'VS Code extension', 'npm CLI · MCP server', 'GitHub Auto-PR', 'Custom Dashboard Builder', 'Engineer Wall · Wall of Tenants', 'Setup in 15 minutes'] },
            ].map((p, i) => {
              const Ic = p.Icon;
              return (
                <div key={i} className="rounded-2xl border border-white/10 bg-slate-900/50 hover:border-emerald-400/60 hover:bg-emerald-500/[0.04] hover:-translate-y-0.5 transition-all p-5">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                      <Ic className="w-4 h-4 text-emerald-300" />
                    </div>
                    <h3 className="text-sm font-bold text-white tracking-tight">{p.pillar}</h3>
                  </div>
                  <ul className="space-y-1 text-[12px] text-slate-300 leading-relaxed mb-3">
                    {p.items.map((it) => (
                      <li key={it} className="flex items-start gap-1.5">
                        <span className="text-emerald-400 mt-1 shrink-0">▸</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="text-[11px] text-emerald-300 font-semibold inline-flex items-center gap-1">
                    Open pillar <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───── 6. MERGED MOAT + TOTAL COST OF AI ───── */}
      <section className="py-12 px-4 border-t border-white/5 relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 mb-4 bg-emerald-500/10">
              Total Cost of AI · the moat
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05] text-white">
              You spent <span className="text-emerald-400">$1M on AI</span>. Most tools see one slice.
            </h2>
            <p className="mt-3 text-base text-slate-300">In 2026 total AI spend is <strong className="text-white">three categories deep</strong>: cloud LLM tokens (40%), redundant SaaS AI add-ons (30%), cross-cloud egress (30%). CARTIE sees all three.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-6">
            {[
              { pct: '40%', title: 'Cloud LLM tokens', sub: 'OpenAI · Anthropic · Google · Mistral · Meta', color: 'border-emerald-500/40 bg-emerald-500/10' },
              { pct: '30%', title: 'Redundant SaaS AI add-ons', sub: 'Notion AI, Canva AI, Copilot, Claude Pro, …', color: 'border-amber-500/40 bg-amber-500/10' },
              { pct: '30%', title: 'Cross-cloud egress', sub: 'AWS → GCP/Azure to feed the models', color: 'border-rose-500/40 bg-rose-500/10' },
            ].map((c, i) => (
              <div key={i} className={`rounded-2xl border-2 ${c.color} p-5 text-center`}>
                <div className="text-4xl font-extrabold text-white">{c.pct}</div>
                <div className="text-sm font-semibold text-white mt-1">{c.title}</div>
                <div className="text-[11px] text-slate-400 mt-1">{c.sub}</div>
              </div>
            ))}
          </div>
          <div className="max-w-5xl mx-auto h-4 rounded-full overflow-hidden flex border border-white/10">
            <div className="bg-emerald-500" style={{ width: '40%' }} />
            <div className="bg-amber-500" style={{ width: '30%' }} />
            <div className="bg-rose-500" style={{ width: '30%' }} />
          </div>
        </div>
      </section>

      {/* ───── 7. WALKTHROUGH (compact) ───── */}
      <section className="py-12 px-4 border-t border-white/5 bg-slate-900/40">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-3 bg-emerald-500/10">
              <Play className="w-3 h-3 fill-current" /> 30-second walkthrough
            </div>
            <h2 className="text-3xl font-extrabold text-white">Watch CARTIE find <span className="text-emerald-400">$8,400/mo</span> in 30 seconds.</h2>
          </div>
          <div className="max-w-3xl mx-auto rounded-2xl border border-emerald-500/30 bg-slate-900 aspect-video flex items-center justify-center">
            <div className="text-center text-slate-400">
              <Play className="w-12 h-12 mx-auto fill-current text-emerald-400/60 mb-2" />
              <span className="text-sm">[walkthrough video loop — animated in production]</span>
            </div>
          </div>
        </div>
      </section>

      {/* ───── 8. FREE TOOLS strip (compact) ───── */}
      <section className="py-12 px-4 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-3 bg-emerald-500/10">
              <Sparkles className="w-3 h-3" /> 7 free tools live · no signup
            </div>
            <h2 className="text-3xl font-extrabold text-white">Try us before you <span className="text-emerald-400">trust us</span>.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['AI Cost Calculator', 'Reality Check', 'PR Cost Predictor', 'Savings Calculator', 'Snowflake Health', 'Prompt Cost Predictor', 'Egress Predictor', 'Fine-Tuning ROI'].map((t, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-slate-900/50 p-4 hover:border-emerald-500/40 transition-all">
                <Calculator className="w-4 h-4 text-emerald-400 mb-2" />
                <div className="text-sm font-semibold text-white">{t}</div>
                <div className="text-[10px] text-emerald-300 mt-1">LIVE · 60s</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── 9. DEVELOPER-LOVED horizontal pills ───── */}
      <section className="py-10 px-4 border-y border-emerald-500/10 bg-slate-950/60">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-4 bg-emerald-500/10">
            <Code2 className="w-3 h-3" /> Engineer-first · zero migration
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">We meet you where you <span className="text-emerald-400">already work</span>.</h2>
          <div className="flex flex-wrap gap-3 justify-center mt-5">
            <div className="inline-flex items-center gap-2 bg-slate-900 border border-emerald-500/30 rounded-full px-4 py-2 hover:border-emerald-400 transition-all">
              <Workflow className="w-4 h-4 text-emerald-400" />
              <code className="text-xs text-emerald-300 font-mono">npx @cartieai/cli why</code>
            </div>
            <div className="inline-flex items-center gap-2 bg-slate-900 border border-emerald-500/30 rounded-full px-4 py-2">
              <Bot className="w-4 h-4 text-emerald-400" />
              <code className="text-xs text-emerald-300 font-mono">MCP server · Claude · Cursor</code>
            </div>
            <div className="inline-flex items-center gap-2 bg-slate-900 border border-emerald-500/30 rounded-full px-4 py-2">
              <GitBranch className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-white font-semibold">GitHub Auto-PR</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-slate-900 border border-emerald-500/30 rounded-full px-4 py-2">
              <span className="text-xs text-white font-semibold">Slack <code className="font-mono text-emerald-300">@cartie</code></span>
            </div>
            <div className="inline-flex items-center gap-2 bg-slate-900 border border-emerald-500/30 rounded-full px-4 py-2">
              <span className="text-xs text-white font-semibold">Monday Brief · Resend</span>
            </div>
          </div>
        </div>
      </section>

      {/* ───── 10. 24 SMART ENGINES grid (compact) ───── */}
      <section className="py-12 px-4 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-3 bg-emerald-500/10">
              <Cpu className="w-3 h-3" /> 26 proprietary engines · cartie-only
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">The <span className="text-emerald-400">smart stuff</span> under the hood.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['Retry storm', 'Cache miss', 'Smart router', 'Idle hunter', 'Egress map', 'Zombie', 'Cost DNA', 'Heatmap', 'Drift', 'Reasoning', 'Arbitrage', 'Shadow AI', 'Carbon', 'Anomaly', 'Autopsy', 'Negotiator', 'Egress predict', 'PR predict', 'Sub-Z', 'Pilot gate', 'Overdrive', 'Burning', 'Tenants', 'Auto-tag'].map((name, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-slate-900/50 p-3 hover:border-emerald-500/40 hover:-translate-y-0.5 transition-all">
                <Layers className="w-4 h-4 text-emerald-400 mb-1.5" />
                <div className="text-xs font-semibold text-white">Engine #{i + 1}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── 11. BUDGET NEGOTIATOR (Sub-System Z) ───── */}
      <section className="py-12 px-4 border-t border-white/5 bg-slate-900/30">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-3 bg-emerald-500/10">
              <ShieldCheck className="w-3 h-3" /> Sub-System Z™ · category-defining
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">The <span className="text-emerald-400">Budget Negotiator</span></h2>
            <p className="text-base text-slate-300">Pre-signed budget envelopes at PR-time. Finance signs once. Engineering ships fast. CI blocks over-budget merges before they hit prod.</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-300">
              <li className="flex items-center gap-2"><Target className="w-4 h-4 text-emerald-400" /> Rego-style policies in your repo</li>
              <li className="flex items-center gap-2"><Target className="w-4 h-4 text-emerald-400" /> Shadow Mode — 30 days non-blocking</li>
              <li className="flex items-center gap-2"><Target className="w-4 h-4 text-emerald-400" /> Sovereignty PR Guardrail (GDPR / PIPEDA / DPDP)</li>
              <li className="flex items-center gap-2"><Target className="w-4 h-4 text-emerald-400" /> Strategic Overdrive — time-boxed expansions</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-500/30 bg-slate-950 p-5">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono mb-2">contract.json</div>
            <pre className="text-xs text-emerald-300 font-mono leading-relaxed overflow-x-auto">{`{
  "envelope": "ml-platform-q2",
  "cap_usd": 20000,
  "signers": ["cfo@hooli", "vp-eng@hooli"],
  "valid_until": "2026-06-30"
}`}</pre>
          </div>
        </div>
      </section>

      {/* ───── 12. HOW YOUR WEEK LOOKS ───── */}
      <section className="py-12 px-4 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">How <span className="text-emerald-400">your week</span> looks with CARTIE.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { step: 'MON', title: 'Brief lands', body: 'Monday Morning Brief in Slack/email · last week wins + open criticals' },
              { step: 'TUE', title: 'PR cost shown', body: 'Every PR shows predicted $/mo cost · merge blocked if over envelope' },
              { step: 'WED', title: 'Auto-PR fix', body: 'CARTIE opens a Terraform PR · confidence 91% · risk low · 15 min' },
              { step: 'FRI', title: 'Margin closes', body: 'Reasoning Efficiency dashboard · Cost / resolved intent down 18%' },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-slate-900/40 p-5">
                <div className="text-[10px] font-bold tracking-widest text-emerald-300 uppercase">{s.step}</div>
                <div className="text-base font-bold text-white mt-1">{s.title}</div>
                <div className="text-xs text-slate-400 mt-2">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── 13. FINAL CTA ───── */}
      <section className="py-12 px-4 border-t border-white/5 bg-slate-950 bg-gradient-to-b from-slate-950 via-emerald-950/20 to-slate-950">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-4 bg-emerald-500/10">
            <Flame className="w-3 h-3" /> Stop burning · start shipping
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">Cut your bill in <span className="text-emerald-400">14 days</span>. Or we refund.</h2>
          <p className="text-base text-slate-300 mb-7">Contractually binding ROI pledge. Read-only audit. Stateless. SOC 2.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-8 py-4 rounded-xl shadow-lg shadow-emerald-500/30 inline-flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Start the pilot — $199/mo
            </button>
            <button className="border border-emerald-400/40 hover:bg-emerald-500/10 text-emerald-200 font-semibold px-8 py-4 rounded-xl">Book a 15-min call</button>
          </div>
        </div>
      </section>

      {/* ───── 14. FOOTER ───── */}
      <footer className="border-t border-white/5 px-4 py-10 bg-slate-950">
        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-6 text-xs text-slate-400">
          <div>
            <div className="text-white font-bold mb-2">CARTIE<span className="text-emerald-400">AI</span></div>
            <p className="text-[11px]">The FinOps Operating System for AI-first companies.</p>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Engines</div>
            <ul className="space-y-1">
              <li>Reasoning Efficiency <span className="text-emerald-400">NEW</span></li>
              <li>Token Arbitrage <span className="text-emerald-400">NEW</span></li>
              <li>Shadow AI Audit <span className="text-emerald-400">NEW</span></li>
              <li>GPU Capacity Exchange <span className="text-emerald-400">NEW</span></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Clouds</div>
            <ul className="space-y-1">
              <li>AWS · Azure · GCP</li>
              <li>Snowflake · Databricks</li>
              <li>DigitalOcean · K8s</li>
              <li>+ 9 more integrating</li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Trust</div>
            <ul className="space-y-1">
              <li>SOC 2</li>
              <li>Algorithm-Correctness manifest</li>
              <li>Honest Scorecard</li>
              <li>ROI Pledge</li>
            </ul>
          </div>
        </div>
      </footer>

      {/* ───── DECISION PANEL ───── */}
      <div className="bg-emerald-500 text-slate-950 px-6 py-6 text-center">
        <h3 className="text-2xl font-extrabold mb-2">Like what you see?</h3>
        <p className="text-sm mb-4">Reply <strong>"Yes do it"</strong> and I'll apply this exact structure to <code className="bg-slate-950 text-emerald-300 px-2 py-0.5 rounded font-mono text-[11px]">/app/frontend/src/pages/Landing.js</code>. Or reply with adjustments and I'll iterate.</p>
        <Link to="/" className="inline-flex items-center gap-2 bg-slate-950 text-emerald-300 rounded-full px-5 py-2 text-sm font-semibold hover:bg-slate-900">
          <Eye className="w-4 h-4" /> Compare with current /
        </Link>
      </div>
    </div>
  );
};

export default LandingV34Preview;
